/**
 * Who is allowed to do what.
 *
 * Five levels. The four job roles come from OHC's own list, and master sits above them
 * for the three people who hold the master accounts (answers 2 and 149). The IT role in
 * their staff sheet is a job title, not a level: that person holds a master account
 * (answer 150).
 *
 * The rules below are theirs, not ours:
 *   - master and admin can verify a record, and may verify their own entry (67, 68)
 *   - a payment voucher can be raised by master or admin, a claim by master only (44)
 *   - only a master can delete (71)
 *   - only a master can lock or unlock a period (70)
 * Everything else follows from those.
 */
export const Role = {
    SalesStaff: "sales_staff",
    Supervisor: "supervisor",
    Manager: "manager",
    Admin: "admin",
    Master: "master",
};
export const ALL_ROLES = [
    Role.SalesStaff,
    Role.Supervisor,
    Role.Manager,
    Role.Admin,
    Role.Master,
];
export function isRole(value) {
    return typeof value === "string" && ALL_ROLES.includes(value);
}
export const Permission = {
    SaleCreate: "sale.create",
    SaleVoid: "sale.void",
    SaleReverse: "sale.reverse",
    BuybackCreate: "buyback.create",
    StockView: "stock.view",
    StockMove: "stock.move",
    StockAdjust: "stock.adjust",
    StockCount: "stock.count",
    PurchaseCreate: "purchase.create",
    VoucherCreate: "voucher.create",
    InvoiceCreate: "invoice.create",
    ClaimCreate: "claim.create",
    ClaimApprove: "claim.approve",
    BankImport: "bank.import",
    BankMatch: "bank.match",
    RecordVerify: "record.verify",
    RecordDelete: "record.delete",
    PeriodLock: "period.lock",
    CostView: "cost.view",
    ReportView: "report.view",
    PersonalBookViewAll: "personal_book.view_all",
    UserManage: "user.manage",
    CompanyManage: "company.manage",
    AccountManage: "account.manage",
    ContactManage: "contact.manage",
    ProductManage: "product.manage",
    CategoryManage: "category.manage",
    // D, 8 October 2026: what the booth roles may not do.
    /** Accounts, balances, suppliers and staff balances: the money side of the business. */
    FinanceView: "finance.view",
    /** Anything that sends a picture or a file to the company's OpenAI account. */
    AiRead: "ai.read",
    /** A product's selling price, which a supervisor may set without managing the catalogue. */
    PriceSet: "price.set",
    /** Each sale's cost and profit, day by day: the System administrator only (D, 9 October 2026). */
    ProfitView: "profit.view",
};
/**
 * Sales staff sell, and that is all (D, 1 November 2026: "Sales staff is only allowed to sell
 * and thats all. Don't let them see anything else"). Stock is readable because the selling
 * screen shows what is on hand; no counting, no categories, no pages of the desk.
 */
const SALES_STAFF = [Permission.SaleCreate, Permission.BuybackCreate, Permission.StockView];
/**
 * A supervisor is a salesperson who can also void a sale and change a selling price, and
 * nothing more (D, 8 October 2026): no accounts, suppliers, purchases, costs or claims. A
 * customer's invoice for a sale is still theirs to print: it is the sale, not the books.
 */
const SUPERVISOR = [
    ...SALES_STAFF,
    Permission.StockCount,
    Permission.CategoryManage,
    Permission.SaleVoid,
    Permission.PriceSet,
    Permission.InvoiceCreate,
];
const MANAGER = [
    ...SUPERVISOR,
    Permission.StockMove,
    Permission.PurchaseCreate,
    Permission.ClaimCreate,
    Permission.CostView,
    Permission.ProductManage,
    Permission.ContactManage,
    Permission.FinanceView,
    Permission.AiRead,
    Permission.SaleReverse,
    Permission.StockAdjust,
    Permission.BankImport,
    Permission.BankMatch,
    Permission.ReportView,
];
const ADMIN = [
    ...MANAGER,
    Permission.VoucherCreate,
    Permission.RecordVerify,
    Permission.AccountManage,
];
const MASTER = [
    ...ADMIN,
    Permission.ClaimApprove,
    Permission.RecordDelete,
    Permission.PeriodLock,
    Permission.PersonalBookViewAll,
    Permission.UserManage,
    Permission.CompanyManage,
    Permission.ProfitView,
];
const BY_ROLE = {
    [Role.SalesStaff]: Object.freeze([...new Set(SALES_STAFF)]),
    [Role.Supervisor]: Object.freeze([...new Set(SUPERVISOR)]),
    [Role.Manager]: Object.freeze([...new Set(MANAGER)]),
    [Role.Admin]: Object.freeze([...new Set(ADMIN)]),
    [Role.Master]: Object.freeze([...new Set(MASTER)]),
};
export function permissionsFor(role) {
    return BY_ROLE[role];
}
export function can(role, permission) {
    return BY_ROLE[role].includes(permission);
}
/**
 * Whether someone may look at another person's own books.
 *
 * Stock a person bought with their own money is that person's stock until the company
 * pays for it (answer 139). They see it, and the masters see it, so the masters can run
 * the informal side. Other staff do not.
 */
export function canSeePersonalBookOf(viewer, ownerPersonId) {
    if (viewer.personId === ownerPersonId)
        return true;
    return can(viewer.role, Permission.PersonalBookViewAll);
}
//# sourceMappingURL=roles.js.map